import { format, isValid, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { z } from 'zod/v4'
import { AdaptedProduct, Product } from '@skop-app/lib-domain/types'
import type { Order, OrderModel } from '@skop-app/lib-domain/types'
import { formatPrice, matchLabel } from '@skop-app/lib-domain/utils'
import { parseSaleTaxRate, readSaleMetadata } from '@skop-app/lib-domain/shop'

import type { OrderModalValues } from '@/types'
import { getOrderAggregateCurrency } from '@/features/quote/lib/orderCurrency'

import {
  buildCanonicalOrderProduct,
  getOrderProductDimensionsLabel,
} from '@/features/quote/lib/orderProducts'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeLineQuantity = (value: unknown): number | undefined => {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const parsed = normalizeLineQuantity(entry)
      if (parsed !== undefined) return parsed
    }
    return undefined
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (isRecord(value)) {
    return (
      normalizeLineQuantity(value.number) ??
      normalizeLineQuantity(value.value) ??
      normalizeLineQuantity(value.label) ??
      normalizeLineQuantity(value.amount)
    )
  }

  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  if (!trimmed) return undefined
  const parsed = Number(trimmed.replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : undefined
}

const readProductQuantity = (value: unknown): number | undefined => {
  if (!isRecord(value)) return undefined

  const properties = value.properties
  const quantityProperty = Array.isArray(properties)
    ? properties.find(
        (entry): entry is Record<string, unknown> =>
          isRecord(entry) && entry.key === 'quantity.number',
      )
    : undefined
  const propertyQuantity = quantityProperty
    ? normalizeLineQuantity(quantityProperty.values)
    : isRecord(properties)
      ? normalizeLineQuantity(properties['quantity.number'])
      : undefined

  return (
    normalizeLineQuantity(value.quantity) ??
    normalizeLineQuantity(value['quantity.number']) ??
    propertyQuantity ??
    readProductQuantity(value.rawProduct)
  )
}

const genericItemAdapter = (
  product: Product | AdaptedProduct | Record<string, unknown>,
  quantity?: unknown,
  fallbackCurrency?: string,
) => {
  const normalizedProduct = buildCanonicalOrderProduct({
    source: product as Record<string, unknown>,
    fallbackPriceCurrency: fallbackCurrency,
  })
  const price = Number(normalizedProduct.rawPrice ?? 0) || 0
  const lineQuantity =
    normalizeLineQuantity(quantity) ?? readProductQuantity(product) ?? 1
  const lineTotal = Number((price * lineQuantity).toFixed(2))
  const unit = normalizedProduct.quantity.unit

  return {
    id: normalizedProduct.id,
    productID: normalizedProduct.productID,
    projectID: normalizedProduct.projectID,
    byUserID: normalizedProduct.byUserID,
    name: normalizedProduct.name ?? 'Nom manquant',
    nature: normalizedProduct.nature,
    pictures: orderPictureAdapter(normalizedProduct.pictures),
    quantity: lineQuantity,
    remainingQuantity: normalizedProduct.quantity.number ?? 0,
    unit,
    price: formatPrice({
      amount: price,
      currency: normalizedProduct.price.currency,
    }),
    total: formatPrice({
      amount: lineTotal,
      currency: normalizedProduct.price.currency,
    }),
    currency: normalizedProduct.price.currency,
    rawPrice: price,
    rawTotal: lineTotal,
    ref: normalizedProduct.ref,
    model: normalizedProduct.model,
    brand: normalizedProduct.brand,
    category: normalizedProduct.category,
    dimensions: getOrderProductDimensionsLabel(normalizedProduct),
    condition: normalizedProduct.condition,
    provenance: normalizedProduct.provenance,
    rawProduct: normalizedProduct.rawProduct ?? normalizedProduct,
  }
}

const productItemsAdapter = (items, fallbackCurrency: string) => {
  try {
    const productString = JSONSafeParse(items) ?? []

    // Return empty array for invalid input
    if (!Array.isArray(productString)) {
      return []
    }

    // Handle empty array
    if (productString.length === 0) {
      return []
    }

    // Check first item structure to determine format
    const firstItem = productString[0]

    // Case 1: { product, quantity } structure
    if (firstItem?.product) {
      // Handle product unit if the unit is a uuid with safeParse
      productString.forEach(({ product }) => {
        if (
          product?.quantity?.unit &&
          z.uuid().safeParse(product.quantity.unit).success
        ) {
          product.quantity.unit = 'Unité'
        }
      })

      return productString
        .filter((item) => item?.product) // Ensure product exists
        .map(({ product, quantity }) =>
          genericItemAdapter(product, quantity, fallbackCurrency),
        )
    }

    // Case 2: Direct product objects
    // Handle product unit if the unit is a uuid with safeParse
    productString.forEach((product) => {
      if (
        product?.quantity?.unit &&
        z.uuid().safeParse(product.quantity.unit).success
      ) {
        product.quantity.unit = 'Unité'
      }
    })

    return productString
      .filter((item) => item?.id) // Basic validation that it's a product
      .map((item) => genericItemAdapter(item, undefined, fallbackCurrency))
  } catch (e) {
    console.error('Error in productItemsAdapter:', e)
    return []
  }
}

const pickupDateFormated = (dates: unknown): string[] => {
  if (!Array.isArray(dates)) {
    return []
  }
  return dates
    .filter((date): date is string => {
      if (typeof date !== 'string') {
        return false
      }
      const parsed = parseISO(date)
      return isValid(parsed)
    })
    .map((date) => {
      try {
        return format(parseISO(date), "dd/MM/yyyy 'à' HH:mm", { locale: fr })
      } catch (e) {
        console.error('Error formatting date:', date, e)
        return null
      }
    })
    .filter((date): date is string => date !== null)
}

const orderPictureAdapter = (
  pictures: string[] | string | undefined,
): string[] => {
  const picturesArray = Array.isArray(pictures) ? pictures : [pictures]
  const picturesString = picturesArray?.[0] ? picturesArray?.[0] : 'default.png'
  return [picturesString]
}

const JSONSafeParse = (json: string) => {
  try {
    return JSON.parse(json)
  } catch {
    return null
  }
}

export type CurrencyOrder = Order & {
  organizationMasterID?: string | null
  organization?: {
    id?: string | null
    parentID?: string | null
    masterID?: string | null
  } | null
  orderUserId?: string | null
  toUserID?: string | null
  currency: string
  taxCurrency: string
  vatPercentage?: { label: string; value: number }
}

export const orderAdapter = (
  params: OrderModel & {
    organizationMasterID?: string | null
    organization?: {
      id?: string | null
      parentID?: string | null
      masterID?: string | null
    } | null
    orderUserId?: string | null
    toUserID?: string | null
    toUser?: OrderModel['toUser'] & Partial<NonNullable<OrderModel['user']>>
    toOrganization?: NonNullable<OrderModel['shop']['organization']> & {
      id?: string | null
    }
  },
): CurrencyOrder => {
  const {
    id,
    billingAddress,
    deliveryAddress,
    status,
    type,
    requestType,
    stripeID,
    shop,
    orderUserId,
    user,
    toUser,
    toUserID,
    toOrganization,
    name,
    email,
    dates,
    message,
    phone,
    items,
    tax,
    total,
    createdAt,
    updatedAt,
    yousignStatus,
    yousignID,
    customer,
    document,
    metadata,
    organizationID,
    organization,
    organizationMasterID,
    managerID,
    toOrganizationID,
  } = params

  const title = matchLabel(requestType, 'request').shortLabel

  // Recipient relations persist even when a monetary issuer has no shop.
  const isMonetaryDocument = type === 'QUOTE' || type === 'CART'
  const recipientOrganization =
    isMonetaryDocument &&
    toOrganizationID &&
    toOrganization?.id === toOrganizationID
      ? toOrganization
      : undefined
  const recipientUser = toUserID && toUser?.id === toUserID ? toUser : undefined
  const personalRecipient =
    isMonetaryDocument && !toOrganizationID ? recipientUser : undefined
  const recipientProfile =
    recipientOrganization ??
    (personalRecipient
      ? {
          ...personalRecipient,
          name: personalRecipient.company || personalRecipient.name,
        }
      : undefined)
  const shopAttributes = shop?.id
    ? {
        id: shop.id,
        name: shop?.company,
        contacts: shop?.contacts ?? [],
        phone: shop?.phone,
        ownerID: shop?.userID,
        user: shop?.user,
        organization: shop?.organization,
        organizationID: shop?.organizationID,
        organizationMasterID: shop?.organizationMasterID,
        logo: shop?.logo,
      }
    : recipientProfile
      ? {
          id: '',
          name: recipientProfile.name ?? '',
          contacts: recipientProfile.email ? [recipientProfile.email] : [],
          phone: recipientProfile.phone ?? '',
          ownerID: recipientUser?.id ?? '',
          user: recipientUser ?? { id: '', name: '' },
          organization: recipientProfile,
          organizationID: recipientOrganization ? toOrganizationID : undefined,
          logo: recipientOrganization?.logo,
        }
      : {
          id: '',
          name: '',
          contacts: [],
          phone: '',
          ownerID: '',
          user: {
            id: '',
            name: '',
          },
        }

  const fallbackCurrency = total?.currency || 'EUR'
  const productItems = productItemsAdapter(items, fallbackCurrency)
  // Requests carry source line units without a monetary total. Resolve only a
  // homogeneous unit; missing line currencies still use the legacy EUR reader.
  const currency =
    total?.currency ||
    (type === 'FAVORITE'
      ? getOrderAggregateCurrency(
          {
            items: productItems as unknown as OrderModalValues['items'],
            currency: { value: fallbackCurrency },
          } as OrderModalValues,
          false,
        )
      : undefined) ||
    fallbackCurrency
  const taxCurrency = tax?.currency || currency
  const taxAmount = tax?.amount == null ? undefined : Number(tax.amount) || 0
  const totalAmount =
    total?.amount == null ? undefined : Number(total.amount) || 0
  const snapshotRate = readSaleMetadata(metadata, 'taxRate')
  const subtotal =
    totalAmount === undefined ? undefined : totalAmount - (taxAmount ?? 0)
  const taxRate =
    snapshotRate !== undefined
      ? parseSaleTaxRate(snapshotRate)
      : subtotal !== undefined && subtotal > 0
        ? parseSaleTaxRate(
            Number((((taxAmount ?? 0) / subtotal) * 100).toFixed(6)),
          )
        : null
  const customerName =
    customer?.firstName || customer?.lastName
      ? `${customer?.firstName} ${customer?.lastName}`
      : null
  const customerEmail = customer?.email ?? null

  return {
    id,
    billingAddress,
    deliveryAddress,
    type,
    title: title ?? 'Commande',
    stripeID,
    shop: shopAttributes,
    orderUserId,
    userID: user?.id,
    user: user,
    toUser: toUser,
    toUserID,
    name: name ?? customerName ?? user?.name ?? '(supprimé)',
    email: email ?? customerEmail ?? user?.email ?? '',
    company: user?.company,
    SIRET: user?.SIRET,
    address: user?.address,
    city: user?.city,
    zipcode: user?.zipcode,
    country: user?.country,
    pickupDate: dates?.length ? pickupDateFormated(dates) : [],
    message,
    phone: user?.phone ?? phone,
    items: productItems,
    statusFormatted: matchLabel(status, 'orderStatus'),
    status,
    currency,
    taxCurrency,
    vatPercentage:
      taxRate !== null ? { label: String(taxRate), value: taxRate } : undefined,
    vat:
      taxAmount === undefined
        ? undefined
        : formatPrice({ amount: taxAmount, currency: taxCurrency }),
    subTotal:
      subtotal === undefined
        ? undefined
        : formatPrice({ amount: subtotal, currency }),
    total:
      totalAmount === undefined
        ? undefined
        : formatPrice({ amount: totalAmount, currency }),
    rawTotal: totalAmount,
    rawTax: taxAmount,
    date: format(new Date(createdAt), 'P p', { locale: fr }),
    requestType,
    updatedAt,
    createdAt,
    yousignStatus,
    yousignID,
    customer,
    document,
    organizationID,
    organization,
    organizationMasterID,
    managerID,
    toOrganizationID,
    metadata: metadata?.map((item) => ({
      key: item.key,
      type: item.type,
      value: JSONSafeParse(item.value ?? '{}'),
    })),
  }
}
